import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useLocation } from "react-router-dom";

import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { type SxProps, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { datadogLogs } from "@datadog/browser-logs";
import { throttle } from "lodash";
import { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  AMBIENT_SCRIBE_PAUSED_FEATURE,
  AMBIENT_SCRIBE_VERIFIED_MICROPHONES
} from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import {
  selectCurrentUser,
  selectFeatures,
  showFeatureDialog
} from "features/auth/auth-slice";
import {
  selectHeaderAnimationInProgress,
  selectIsHeaderCollapsed
} from "features/patients/patient-header/patient-header-slice";
import InsertScribeDialog from "features/patients/sections/patient-chart-notes/insert-scribe-dialog/insert-scribe-dialog.component";
import {
  showInsertScribe,
  showInsertScribeForChartNoteId
} from "features/patients/sections/patient-chart-notes/patient-chart-notes.slice";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import { glowBlueShadowSx, trialOrangeShadowSx } from "features/paywall/utils";
import { usePauseAmbientScribeMutation } from "services/ambient-scribe";
import { useGetPatientQuery } from "services/patients";
import { getFullName } from "utils/common";
import { getTrialInfo, shouldShowTrialBanner } from "utils/trial-status.util";
import { viteEnv } from "utils/vite-env.util";

import useCheckPaywallFeatureAvailability from "../../paywall/use-check-paywall-feature-availability.hook";
import MicUnverifiedPromptDialog from "../components/mic-unverified-prompt-dialog.component";
import WaveVolumeIndicator from "../components/wave-volume-indicator.component";
import WidgetButton from "../components/widget-button.component";
import { useMicrophoneAssessment } from "../hooks/use-microphone-verification.hook";
import {
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_PAUSED,
  RECORDING_STATE_SAVING,
  RECORDING_STATE_STOPPED,
  WIDGET_RETURN_LATER_BUTTON_TOOLTIP
} from "../medical-scribe-constants";
import {
  changeScribeDeviceSettings,
  selectMedicalScribeDeviceSettings,
  selectMedicalScribeIsMicActive,
  selectMedicalScribeMicVolume,
  selectMedicalScribeRecordingState,
  selectShowMedicalScribeDeviceDialog,
  selectStreamingIsReconnecting,
  selectStreamingScribeId,
  selectStreamingTranscript
} from "../medical-scribe.slice";
import {
  type MicrophoneInput,
  requestMicrophonePermissionForEnumeration
} from "../microphone.utils";
import PurchaseAmbientScribeDialog from "../purchase-ambient-scribe-dialog.component";
import { useMedicalScribe } from "../use-medical-scribe.hook";
import { getPatientIdFromPathname } from "../utils";
import DeviceSettingsButton from "./device-settings/device-settings-button.component";
import DeviceSettingsModal from "./device-settings/device-settings-modal.component";
import MicrophoneSetupModal from "./microphone-setup-modal/microphone-setup-modal.component";
import StopRecordingButton from "./stop-recording-button.component";
import { useMicrophoneSetup } from "./use-microphone-setup.hook";

// Constants
const WIDGET_ANCHOR_GAP = 10;
const WIDGET_TOP_OFFSET = -5;
const REPOSITIONING_PHASES = {
  PHASE_1_INTERVAL: 100,
  PHASE_2_START: 2000,
  PHASE_2_INTERVAL: 250,
  PHASE_3_START: 5000,
  PHASE_3_INTERVAL: 500,
  PHASE_4_START: 10000,
  PHASE_4_INTERVAL: 1000,
  PHASE_5_START: 20000,
  PHASE_5_INTERVAL: 2000,
  TOTAL_DURATION: 45000
} as const;
const PATIENT_LOAD_TIMEOUTS = [50, 150, 300, 500, 800, 1200] as const;

interface MedicalScribeRecordingWidgetProps {
  rightAnchorElementSelector?: string;
  mode?: "fixed" | "draggable";
  isFullSize?: boolean;
  suppressInsertScribeDialog?: boolean;
  editor?: Editor;
}

const isLocalEnv = viteEnv.VITE_ENV === "local";

const setupMicButtonSx = {
  color: "white",
  fontSize: "16px",
  fontWeight: 400,
  textDecoration: "underline",
  p: 0,
  minWidth: 0,
  mr: -1.5,
  ml: -1.5,
  lineHeight: 1,
  textTransform: "none",
  "&:hover": {
    color: "rgba(255,255,255,0.72)",
    background: "transparent",
    textDecoration: "underline"
  }
} as const;

const transcriptTextSx = {
  position: "absolute",
  top: "-37px",
  left: "0",
  width: "342px",
  height: "auto",
  minHeight: "30px",
  maxHeight: "calc(13px * 1.1 * 2 + 8px)",
  borderRadius: "4px",
  background: "rgba(0,0,0,0.7)",
  fontSize: "13px",
  px: 0.5,
  py: 0.25,
  lineHeight: "1.1",
  overflow: "hidden",
  textOverflow: "ellipsis",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  maxWidth: "348px",
  textAlign: "left",
  alignItems: "center",
  display: "flex"
};

export default function MedicalScribeRecordingWidget({
  mode = "draggable",
  rightAnchorElementSelector,
  isFullSize,
  suppressInsertScribeDialog = false,
  editor
}: MedicalScribeRecordingWidgetProps) {
  const settings = useAppSelector(selectMedicalScribeDeviceSettings);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const patientId = getPatientIdFromPathname(location.pathname);
  const [isFetchingFeatures, setIsFetchingFeatures] = useState(false);
  const {
    isSetupComplete,
    getMicSetupCompletedAt,
    markSetupComplete,
    clearMicSetupCompletedAt
  } = useMicrophoneSetup();
  const [showMicSetupModal, setShowMicSetupModal] = useState(false);
  const [showMicUnverifiedPrompt, setShowMicUnverifiedPrompt] = useState(false);
  const startRecordingAfterSetupRef = useRef(false);
  const pendingMicFingerprintRef = useRef<MicrophoneInput | undefined>(
    undefined
  );
  const flags = useFlags();
  const isVerifiedMicrophonesEnabled = Boolean(
    flags[AMBIENT_SCRIBE_VERIFIED_MICROPHONES]
  );
  const currentUser = useAppSelector(selectCurrentUser);
  const { isFirstTime, prepareForAmbientScribeStart } = useMicrophoneAssessment(
    settings.microphone,
    isVerifiedMicrophonesEnabled,
    currentUser?.id,
    getMicSetupCompletedAt
  );

  const captureVisitAbortRef = useRef<AbortController | null>(null);
  const isWidgetMountedRef = useRef(true);

  useEffect(() => {
    isWidgetMountedRef.current = true;
    return () => {
      isWidgetMountedRef.current = false;
      captureVisitAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setIsFetchingFeatures(false);
  }, [patientId]);

  const { show } = useAppSelector(showInsertScribeForChartNoteId);

  const { data: patient, isLoading: isPatientLoading } = useGetPatientQuery(
    { patientId: Number(patientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !patientId || patientId === -1
    }
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));

  const volume = useAppSelector(selectMedicalScribeMicVolume);
  const recordingState = useAppSelector(selectMedicalScribeRecordingState);
  const isReconnecting = useAppSelector(selectStreamingIsReconnecting);

  // Clear the "mic setup done" signal when recording stops so a stale timestamp
  // cannot suppress the mic-check prompt after a future failed scribe.
  useEffect(() => {
    if (recordingState === RECORDING_STATE_STOPPED) {
      clearMicSetupCompletedAt();
    }
  }, [recordingState, clearMicSetupCompletedAt]);

  const showDeviceSettingsDialog = useAppSelector(
    selectShowMedicalScribeDeviceDialog
  );
  const transcript = useAppSelector(selectStreamingTranscript);

  // Trial status logic
  const features = useAppSelector(selectFeatures);
  const featureStatus = features?.[AMBIENT_SCRIBE_FEATURE_KEY];
  const isInTrial = shouldShowTrialBanner(featureStatus);
  const trialInfo = getTrialInfo(featureStatus);
  const isMicActive = useAppSelector(selectMedicalScribeIsMicActive);

  // Debug: Log recording state changes
  useEffect(() => {
    if (isLocalEnv) {
      console.log("Recording state changed to:", recordingState);
    } else {
      try {
        datadogLogs.logger.log("Ambient Scribe", {
          message: "Recording state changed",
          recordingState
        });
      } catch {
        // Silently fail if datadog is not available - this prevents console errors
      }
    }
  }, [recordingState]);

  // Try streaming context first, fall back to old hook
  // const streamingContext = useContext(MedicalScribeStreamingContext);
  const context = useMedicalScribe();

  // Use streaming context if available, otherwise use old hook
  const {
    startRecording,
    pauseRecording,
    resumeRecording,
    resetAfterPaused,
    mediaRecorderRef,
    isAmbientScribeFeatureAvailable,
    setIsAmbientScribeFeatureAvailable,
    backgroundCleanupInFlight
  } = context;

  const isPausedFeatureEnabled = flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false;
  const scribeId = useAppSelector(selectStreamingScribeId);
  const [pauseScribe, { isLoading: isSavingForLater }] =
    usePauseAmbientScribeMutation();

  const handleReturnLater = useCallback(async () => {
    if (!scribeId) return;
    try {
      await pauseScribe({ scribe_id: scribeId }).unwrap();
      resetAfterPaused();
      dispatch(
        setCustomMessage(
          "Recording saved for later — open the scribe list to resume or complete."
        )
      );
      dispatch(setOpen(5000));
    } catch {
      dispatch(
        setCustomMessage(
          "Could not save recording for later. Please try again."
        )
      );
      dispatch(setOpen(4000));
    }
  }, [scribeId, pauseScribe, resetAfterPaused, dispatch]);

  const isHeaderCollapsed = useAppSelector(selectIsHeaderCollapsed);
  const headerAnimationInProgress = useAppSelector(
    selectHeaderAnimationInProgress
  );

  const [anchorPosition, setAnchorPosition] = useState<
    { top?: number; left?: number } | undefined
  >();

  const [isHovered, setIsHovered] = useState(isFullSize);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [hasManuallyDragged, setHasManuallyDragged] = useState(false);
  const draggableRef = useRef<any>(null);

  useEffect(() => {
    setIsHovered(isFullSize);
  }, [isFullSize]);

  // Reset drag position when mode changes
  useEffect(() => {
    setHasManuallyDragged(false);
    // Reset the draggable position
    if (draggableRef.current) {
      draggableRef.current.setState({ x: 0, y: 0 });
    }
  }, [mode]);

  const changeWidgetPosition = useCallback(() => {
    if (mode === "draggable") {
      setAnchorPosition(undefined);
      return;
    }

    // Don't reposition if user has manually dragged in fixed mode
    if (hasManuallyDragged) {
      return;
    }

    // For fixed mode, we need the anchor element
    if (!rightAnchorElementSelector) {
      setAnchorPosition(undefined);
      return;
    }

    requestAnimationFrame(() => {
      const anchorElement = document.querySelector(
        rightAnchorElementSelector
      ) as HTMLElement | null;

      if (!anchorElement) {
        setAnchorPosition(undefined);
        return;
      }

      const anchorRect = anchorElement.getBoundingClientRect();

      // Get the actual widget width from the DOM if available
      const widgetElement = widgetRef.current;
      const actualWidgetWidth = widgetElement
        ? widgetElement.getBoundingClientRect().width
        : 0;

      // Position to the left of the container, with small gap
      const newPosition = {
        top: anchorRect.top + WIDGET_TOP_OFFSET,
        left: anchorRect.left - actualWidgetWidth - WIDGET_ANCHOR_GAP
      };

      setAnchorPosition((prev) => {
        if (
          !prev ||
          prev.top !== newPosition.top ||
          prev.left !== newPosition.left
        ) {
          return newPosition;
        }
        return prev;
      });
    });
  }, [mode, rightAnchorElementSelector, hasManuallyDragged]);

  // Create a throttled version for interval-based repositioning
  // This reduces excessive re-renders during the aggressive repositioning phases
  const changeWidgetPositionThrottled = useRef(
    throttle(() => changeWidgetPosition(), 50, {
      leading: true,
      trailing: true
    })
  );

  // Update throttled function when changeWidgetPosition changes
  useEffect(() => {
    changeWidgetPositionThrottled.current = throttle(
      () => changeWidgetPosition(),
      50,
      { leading: true, trailing: true }
    );
    return () => {
      changeWidgetPositionThrottled.current.cancel();
    };
  }, [changeWidgetPosition]);

  useEffect(() => {
    const targetDiv = document.getElementById("patient-profile-header");
    if (!targetDiv) return;

    let isCallbackScheduled = false;

    const observerCallback = () => {
      if (isCallbackScheduled) return;
      isCallbackScheduled = true;

      requestAnimationFrame(() => {
        isCallbackScheduled = false;
        changeWidgetPosition();
      });
    };

    const observers = {
      patientHeader: new ResizeObserver(observerCallback),
      anchor: rightAnchorElementSelector
        ? new ResizeObserver(observerCallback)
        : undefined,
      widget: widgetRef.current
        ? new ResizeObserver(observerCallback)
        : undefined
    };

    observers.patientHeader.observe(targetDiv);

    // Observe the widget itself for size changes
    if (observers.widget && widgetRef.current) {
      observers.widget.observe(widgetRef.current);
    }

    let anchorElement: Element | null = null;

    // Function to set up anchor element observation
    const setupAnchorObservation = () => {
      if (!observers.anchor || !rightAnchorElementSelector) return;

      const anchorDiv = document.querySelector(rightAnchorElementSelector);
      if (anchorDiv && anchorDiv !== anchorElement) {
        // If we were observing a different element, stop
        if (anchorElement) {
          observers.anchor.unobserve(anchorElement);
        }
        anchorElement = anchorDiv;
        observers.anchor.observe(anchorDiv);
        // Reposition immediately when we find the element
        changeWidgetPosition();
      }
    };

    // Try to observe anchor element immediately
    setupAnchorObservation();

    // Add MutationObserver to watch for when anchor element gets added to DOM
    let mutationObserver: MutationObserver | undefined;
    if (rightAnchorElementSelector) {
      mutationObserver = new MutationObserver((mutations) => {
        let shouldReposition = false;

        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (
                  element.matches?.(rightAnchorElementSelector) ||
                  element.querySelector?.(rightAnchorElementSelector)
                ) {
                  shouldReposition = true;
                }
              }
            });
          }
        });

        if (shouldReposition) {
          // Set up observation for the newly added element
          setupAnchorObservation();
        }
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      observers.patientHeader.disconnect();
      observers.anchor?.disconnect();
      observers.widget?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [changeWidgetPosition, rightAnchorElementSelector]);

  useEffect(() => {
    changeWidgetPosition();
  }, [
    isHeaderCollapsed,
    patient?.id,
    headerAnimationInProgress,
    changeWidgetPosition
  ]);

  // Aggressive repositioning during initial load (handles patient data + feature flags loading)
  // Extended to 45 seconds to account for slow page loads
  useEffect(() => {
    if (mode !== "fixed" || !rightAnchorElementSelector) {
      return;
    }

    let intervalId: NodeJS.Timeout;

    // Helper to create repositioning interval
    const createRepositionInterval = (interval: number) => {
      return setInterval(() => {
        const element = document.querySelector(rightAnchorElementSelector);
        if (element) {
          changeWidgetPositionThrottled.current();
        }
      }, interval);
    };

    // Phase 1: Very aggressive (first 2 seconds) - every 100ms
    // Handles initial page load, patient data, and feature flags
    intervalId = createRepositionInterval(
      REPOSITIONING_PHASES.PHASE_1_INTERVAL
    );

    // Phase 2: High frequency (2-5 seconds) - every 250ms
    const phase2Timeout = setTimeout(() => {
      clearInterval(intervalId);
      intervalId = createRepositionInterval(
        REPOSITIONING_PHASES.PHASE_2_INTERVAL
      );
    }, REPOSITIONING_PHASES.PHASE_2_START);

    // Phase 3: Medium frequency (5-10 seconds) - every 500ms
    const phase3Timeout = setTimeout(() => {
      clearInterval(intervalId);
      intervalId = createRepositionInterval(
        REPOSITIONING_PHASES.PHASE_3_INTERVAL
      );
    }, REPOSITIONING_PHASES.PHASE_3_START);

    // Phase 4: Low frequency (10-20 seconds) - every 1000ms
    const phase4Timeout = setTimeout(() => {
      clearInterval(intervalId);
      intervalId = createRepositionInterval(
        REPOSITIONING_PHASES.PHASE_4_INTERVAL
      );
    }, REPOSITIONING_PHASES.PHASE_4_START);

    // Phase 5: Very low frequency (20-45 seconds) - every 2000ms
    const phase5Timeout = setTimeout(() => {
      clearInterval(intervalId);
      intervalId = createRepositionInterval(
        REPOSITIONING_PHASES.PHASE_5_INTERVAL
      );
    }, REPOSITIONING_PHASES.PHASE_5_START);

    // Stop after 45 seconds total (handles slow page loads)
    const stopTimeout = setTimeout(() => {
      clearInterval(intervalId);
    }, REPOSITIONING_PHASES.TOTAL_DURATION);

    return () => {
      clearInterval(intervalId);
      clearTimeout(phase2Timeout);
      clearTimeout(phase3Timeout);
      clearTimeout(phase4Timeout);
      clearTimeout(phase5Timeout);
      clearTimeout(stopTimeout);
    };
  }, [mode, rightAnchorElementSelector, changeWidgetPosition]);

  // Additional repositioning when patient finishes loading
  // (feature flags may still be loading)
  useEffect(() => {
    if (mode !== "fixed" || !rightAnchorElementSelector || isPatientLoading) {
      return;
    }

    // Patient just finished loading, but feature flags might still be settling
    const timeouts = PATIENT_LOAD_TIMEOUTS.map((delay) =>
      setTimeout(() => changeWidgetPosition(), delay)
    );

    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, [
    mode,
    rightAnchorElementSelector,
    isPatientLoading,
    changeWidgetPosition
  ]);

  const isFixed = useMemo(
    () => mode === "fixed" && Boolean(anchorPosition),
    [mode, anchorPosition]
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isFullSize) return;
    setIsHovered(false);
  }, [isFullSize]);

  const handleDrag = useCallback(
    (e: any, data: { x: number; y: number }) => {
      // Mark as manually dragged when user drags in fixed mode
      if (mode === "fixed" && (data.x !== 0 || data.y !== 0)) {
        setHasManuallyDragged(true);
      }
    },
    [mode]
  );

  const patientFullName = useMemo(() => {
    return patient
      ? getFullName({
          firstName: patient.first_name,
          lastName: patient.last_name,
          middleName: patient.middle_name,
          preferredName: patient.preferred_name,
          title: patient.title,
          suffix: patient.suffix
        })
      : undefined;
  }, [patient]);

  const checkFeatureAvailabilityAsync = useCheckPaywallFeatureAvailability();

  const completeAmbientScribeStartFlow = useCallback(
    async (
      controller: AbortController,
      micEnumerationPromise: Promise<{
        activeDeviceId: string | undefined;
      }>
    ) => {
      try {
        const featurePromise = checkFeatureAvailabilityAsync(
          AMBIENT_SCRIBE_FEATURE_KEY
        );
        const preparePromise = prepareForAmbientScribeStart(
          controller.signal,
          micEnumerationPromise
        );
        const [{ isAvailable }, result] = await Promise.all([
          featurePromise,
          preparePromise
        ]);

        if (!isWidgetMountedRef.current || controller.signal.aborted) {
          return;
        }

        setIsAmbientScribeFeatureAvailable(isAvailable);
        if (!isAvailable) {
          dispatch(showFeatureDialog(AMBIENT_SCRIBE_FEATURE_KEY));
          return;
        }

        if ("cancelled" in result && result.cancelled) {
          return;
        }

        if ("needsMicrophoneSetup" in result && result.needsMicrophoneSetup) {
          startRecordingAfterSetupRef.current = true;
          setShowMicSetupModal(true);
          return;
        }

        if (result.shouldShowMicUnverifiedPrompt) {
          pendingMicFingerprintRef.current = result.microphoneFingerprint;
          setShowMicUnverifiedPrompt(true);
          return;
        }

        startRecording({
          patientId,
          patientFullName,
          chartNoteId: undefined,
          microphoneFingerprint: result.microphoneFingerprint
        });
      } catch (error) {
        if (error instanceof Error && error.cause !== undefined) {
          console.warn(
            "Ambient scribe start failed:",
            error.message,
            error.cause
          );
        }
        if (!isWidgetMountedRef.current) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : "Microphone access was denied or is unavailable. Allow microphone access and try again.";
        dispatch(setCustomMessage(message));
        dispatch(setOpen(8000));
      } finally {
        if (isWidgetMountedRef.current) {
          setIsFetchingFeatures(false);
        }
      }
    },
    [
      checkFeatureAvailabilityAsync,
      dispatch,
      patientFullName,
      patientId,
      prepareForAmbientScribeStart,
      setIsAmbientScribeFeatureAvailable,
      startRecording
    ]
  );

  const handleAmbientScribeStartClick = useCallback(() => {
    captureVisitAbortRef.current?.abort();
    const controller = new AbortController();
    captureVisitAbortRef.current = controller;

    // Plain (non-async) click handler: start getUserMedia synchronously on this stack so
    // strict browsers (Firefox private windows) still treat the call as user-initiated.
    const micEnumerationPromise = requestMicrophonePermissionForEnumeration(
      settings.microphone
    );

    setIsFetchingFeatures(true);
    void completeAmbientScribeStartFlow(controller, micEnumerationPromise);
  }, [completeAmbientScribeStartFlow, settings.microphone]);

  const containerSx = useMemo(() => {
    const isRecordingInProgress =
      recordingState === RECORDING_STATE_IN_PROGRESS;
    const normalBackground = isRecordingInProgress
      ? "hsla(3, 100%, 59%, 0.85)"
      : "hsla(273, 21%, 54%, 0.85)";
    const hoverBackground = isRecordingInProgress
      ? "hsla(3, 100%, 59%, 1)"
      : "hsla(273, 21%, 54%, 1)";
    return {
      bottom: isFixed ? undefined : isMobile ? 60 : 10,
      right: isFixed ? undefined : isMobile ? 10 : "40%",
      left: isFixed ? anchorPosition?.left : undefined,
      top: isFixed ? anchorPosition?.top : undefined,

      boxSizing: "border-box",
      position: "absolute",
      background: normalBackground,
      "&:hover": {
        background: hoverBackground
      },
      color: "white",
      borderRadius: "8px",
      boxShadow: theme.shadows[3],
      px: 1.25,
      py: 1,
      userSelect: "none",
      zIndex: isFixed ? undefined : theme.zIndex.snackbar + 2,
      ...(!isAmbientScribeFeatureAvailable ? glowBlueShadowSx : undefined),
      ...(isInTrial ? trialOrangeShadowSx : undefined)
    } as SxProps;
  }, [
    anchorPosition?.left,
    anchorPosition?.top,
    isFixed,
    isMobile,
    theme.shadows,
    theme.zIndex.snackbar,
    isAmbientScribeFeatureAvailable,
    isInTrial,
    recordingState
  ]);

  const hasMediaRecorder = Boolean(mediaRecorderRef.current);
  const isRecordingActive =
    recordingState === RECORDING_STATE_IN_PROGRESS ||
    recordingState === RECORDING_STATE_PAUSED;

  const handleOpenInsertScribe = useCallback(() => {
    dispatch(showInsertScribe({ chartNoteId: undefined, show: true }));
  }, [dispatch]);

  const fullText = useMemo(
    () => transcript?.map((entry) => entry.text).join(" ") || "",
    [transcript]
  );

  const transcriptPreview = useMemo(() => {
    if (isReconnecting) {
      return "Reconnecting…";
    }
    if (recordingState === RECORDING_STATE_SAVING) {
      return "Please wait…";
    }
    if (recordingState === RECORDING_STATE_PAUSED) {
      return "Paused…";
    }
    if (!fullText) {
      return "";
    }

    // If text is longer than 110 characters, show last 110 with ellipsis
    if (fullText.length > 110) {
      return `…${fullText.slice(-110)}`;
    }

    return fullText;
  }, [fullText, isReconnecting, recordingState]);

  // Full transcript for tooltip (last 1024 characters)
  const fullTranscript = useMemo(() => {
    if (!fullText) {
      return "Listening…";
    }

    // If text is longer than 1024 characters, show last 1024 with ellipsis
    if (fullText.length > 1024) {
      return `…${fullText.slice(-1024)}`;
    }

    return fullText;
  }, [fullText]);

  const renderWidgetButton = useMemo(() => {
    return (
      <>
        {recordingState === RECORDING_STATE_STOPPED && (
          <WidgetButton
            loading={isFetchingFeatures}
            tooltip={
              isFetchingFeatures && backgroundCleanupInFlight
                ? "Finishing previous recording…"
                : "Start Audio Recording"
            }
            startIcon={<MicIcon sx={{ ml: -0.5 }} />}
            onClick={handleAmbientScribeStartClick}
          >
            {(mode !== "fixed" || isHovered) && (
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  transform: "translateY(-2px)"
                }}
              >
                {isFetchingFeatures && backgroundCleanupInFlight
                  ? "Finishing…"
                  : "Capture Visit"}
              </Typography>
            )}
          </WidgetButton>
        )}

        {hasMediaRecorder && isRecordingActive && (
          <WaveVolumeIndicator
            volume={volume}
            isMicActive={isMicActive}
            recordingState={recordingState}
          />
        )}

        {recordingState === RECORDING_STATE_IN_PROGRESS && (
          <WidgetButton
            tooltip="Pause Audio Recording"
            startIcon={<PauseIcon />}
            onClick={pauseRecording}
          />
        )}
        {recordingState === RECORDING_STATE_PAUSED && (
          <WidgetButton
            tooltip="Resume Audio Recording"
            startIcon={<MicIcon />}
            onClick={resumeRecording}
            sx={{ background: misc.sunoGreen }}
          />
        )}
        {isPausedFeatureEnabled &&
          recordingState === RECORDING_STATE_PAUSED && (
            <WidgetButton
              tooltip={WIDGET_RETURN_LATER_BUTTON_TOOLTIP}
              onClick={handleReturnLater}
              loading={isSavingForLater}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                Postpone
              </Typography>
            </WidgetButton>
          )}
        {recordingState === RECORDING_STATE_SAVING && (
          <WidgetButton
            tooltip="Please wait while we save your recording…"
            startIcon={<CircularProgress sx={{ color: "white" }} size={16} />}
            sx={{ cursor: "progress" }}
            onClick={() => {}}
          >
            <Typography
              sx={{ fontSize: "14px", pl: 1, fontWeight: 700, width: "174px" }}
            >
              Please Wait…
            </Typography>
          </WidgetButton>
        )}
        {(isRecordingActive || recordingState === RECORDING_STATE_SAVING) && (
          <StopRecordingButton />
        )}
      </>
    );
  }, [
    hasMediaRecorder,
    isHovered,
    isMicActive,
    isRecordingActive,
    isPausedFeatureEnabled,
    isSavingForLater,
    mode,
    pauseRecording,
    recordingState,
    resumeRecording,
    handleReturnLater,
    volume,
    handleAmbientScribeStartClick,
    isFetchingFeatures,
    backgroundCleanupInFlight
  ]);

  // Dialog must mount even when the floating widget is hidden (!isFullSize pages
  // such as audiology reports). Previously early `return null` skipped this entirely.
  const insertScribeDialog =
    show && Boolean(patientId) && patientId !== -1 && !suppressInsertScribeDialog ? (
      <InsertScribeDialog patientId={Number(patientId)} />
    ) : null;

  if (
    recordingState === RECORDING_STATE_STOPPED &&
    (patientId === -1 || !patient?.id || !patientId)
  ) {
    return insertScribeDialog;
  }

  if (recordingState === RECORDING_STATE_STOPPED && !isFullSize) {
    return insertScribeDialog;
  }

  return (
    <>
      <Draggable
        ref={draggableRef}
        axis="both"
        bounds="body"
        onDrag={handleDrag}
        cancel="button, [data-disable-drag='true']"
      >
        <Tooltip
          title={
            isInTrial
              ? `Trial Status: ${trialInfo.daysRemaining} days remaining`
              : ""
          }
          placement="top"
          disableInteractive
        >
          <Box
            ref={widgetRef}
            sx={containerSx}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {isRecordingActive && settings.showTranscriptionPreview && (
              <Tooltip
                title={fullTranscript}
                placement="top"
                disableInteractive
                arrow
              >
                <Box
                  sx={transcriptTextSx}
                  style={{
                    maxWidth: !fullText ? "215px" : undefined
                  }}
                >
                  {transcriptPreview}
                </Box>
              </Tooltip>
            )}

            <Stack direction="column" alignItems="flex-start" rowGap={0.5}>
              <Stack
                direction="row"
                alignItems="center"
                columnGap={1.25}
                minWidth={
                  recordingState === RECORDING_STATE_SAVING ? 330 : undefined
                }
              >
                {recordingState === RECORDING_STATE_STOPPED &&
                  !isSetupComplete && (
                    <Tooltip title="Test Microphone" disableInteractive>
                      <Button
                        variant="text"
                        size="small"
                        sx={setupMicButtonSx}
                        onClick={() => setShowMicSetupModal(true)}
                        data-disable-drag="true"
                      >
                        Mic Test
                      </Button>
                    </Tooltip>
                  )}

                <DeviceSettingsButton />
                {renderWidgetButton}
                <WidgetButton
                  tooltip="Open Patient Ambient Scribes"
                  startIcon={<UnfoldMoreIcon fontSize="small" />}
                  onClick={handleOpenInsertScribe}
                />
                {/* <Tooltip title="Open Ambient Scribe List" disableInteractive>
                  <IconButton
                    size="small"
                    sx={insertScribeSx}
                    onClick={handleOpenInsertScribe}
                    onTouchEnd={handleOpenInsertScribe}
                  >
                    {isPausedFeatureEnabled ? (
                      <UnfoldMoreIcon fontSize="small" />
                    ) : (
                      <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={0.5}
                      >
                        <AudioFileIcon />
                        <KeyboardArrowDownIcon sx={{ ml: -0.75 }} />
                      </Stack>
                    )}
                  </IconButton>
                </Tooltip> */}
              </Stack>
            </Stack>
          </Box>
        </Tooltip>
      </Draggable>
      {showDeviceSettingsDialog && <DeviceSettingsModal />}
      <MicUnverifiedPromptDialog
        open={showMicUnverifiedPrompt}
        isFirstTime={isFirstTime}
        onRunSetup={() => {
          setShowMicUnverifiedPrompt(false);
          startRecordingAfterSetupRef.current = true;
          setShowMicSetupModal(true);
        }}
        onProceedAnyway={() => {
          setShowMicUnverifiedPrompt(false);
          const microphoneFingerprint = pendingMicFingerprintRef.current;
          pendingMicFingerprintRef.current = undefined;
          startRecording({
            patientId,
            patientFullName,
            chartNoteId: undefined,
            microphoneFingerprint
          });
        }}
        onCancel={() => setShowMicUnverifiedPrompt(false)}
      />
      {showMicSetupModal && (
        <MicrophoneSetupModal
          onClose={() => {
            startRecordingAfterSetupRef.current = false;
            setShowMicSetupModal(false);
          }}
          onComplete={(micId) => {
            markSetupComplete();
            dispatch(
              changeScribeDeviceSettings({ ...settings, microphone: micId })
            );
            if (startRecordingAfterSetupRef.current) {
              startRecordingAfterSetupRef.current = false;
              startRecording({
                patientId,
                patientFullName,
                chartNoteId: undefined
              });
            }
          }}
          currentSettings={settings}
          userId={currentUser?.id ?? 0}
        />
      )}
      {insertScribeDialog}
      <PurchaseAmbientScribeDialog />
    </>
  );
}
